// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { Heading } from '@riseart/common';
import { Section, Wrapper } from '@riseart/layout';
import { useQuery } from 'shared_services/apollo/useQuery';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { FilterBreadcrumbs } from 'shared_components/common/filters/breadcrumbs/Breadcrumbs';
import { FilterSection } from 'shared_components/common/filters/container/Section';
import { WithFilterState } from 'shared_hocs/connect/WithFilterState';
import { filterDomainToStoreKey } from 'shared_hocs/filters/withFilterState';
import { selectByStoreKey } from 'shared_services/redux/selectors/byKey';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { CollectionGrid } from 'shared_components/collections/containers/grid/Grid';
import { CollectionFiltersList } from 'shared_components/collections/containers/filters/List';
import { pagination as PAGINATION_ENUM, filter as FILTER_ENUM } from 'Enum';
import { HOCFilterContainer } from 'shared_hocs/filters/Container';
import HOCFilterPageRoute from 'shared_hocs/routes/FilterPageRoute';
import FILTER_COLLECTION_QUERY from 'shared_data/queries/filter/collection.graphql';
import { convertPayloadToQueryVars } from 'shared_services/riseart/utils/Utils';

import {
  collectionListCls,
  collectionListBannerCls,
} from 'shared_components/pages/collection/List.less';

const { SLIDER } = PAGINATION_ENUM.type;
const ITEMS_PER_PAGE = 5;
const hocConfigOptions = {
  domain: FILTER_ENUM.domain.COLLECTION,
  configMapper: (config, props) => {
    const domainConfig = config[props.domain];

    return {
      [props.domain]: {
        ...domainConfig,
        options: {
          ...domainConfig.options,
          rootUrlPath: props.match.url,
        },
      },
    };
  },
};

const HOCCollectionListFiltersContainer = HOCFilterContainer(
  CollectionFiltersList,
  hocConfigOptions,
);
const CollectionListPage: Function = ({ domain, pageUrl, paginationType }: any) => {
  const storeCode = useStoreCode();
  const { filters, page } = useSelector(selectByStoreKey(filterDomainToStoreKey[domain])) || {};
  const { loading, data } = useQuery(FILTER_COLLECTION_QUERY, {
    skip: !storeCode,
    variables: {
      inputCollectionFilter: convertPayloadToQueryVars(
        { items: ITEMS_PER_PAGE, page, ...filters },
        storeCode,
      ),
    },
  });
  const { items, pagination } = (data && data.filterCollection) || {};

  return (
    <LayoutDefault className={collectionListCls}>
      <FilterSection>
        <WithFilterState domain={domain}>
          {({ medium, style, subject }) => (
            <Wrapper>
              <FilterBreadcrumbs
                filterDomain={domain}
                filters={{ medium, style, subject }}
                config={['medium', 'style', 'subject']}
              />
            </Wrapper>
          )}
        </WithFilterState>
        <Wrapper className={collectionListBannerCls}>
          <FormattedMessage id="components.collections.list_title">
            {(title: string) => (
              <Heading tag="h1" level="2" align="center">
                {title}
              </Heading>
            )}
          </FormattedMessage>
        </Wrapper>
        <Wrapper
          sizes={{
            lg: 'narrow',
            xl: 'narrow',
          }}
        >
          <HOCCollectionListFiltersContainer domain={domain} />
        </Wrapper>
      </FilterSection>
      <Section>
        <Wrapper
          sizes={{
            xs: 'wide',
            sm: 'wide',
            md: 'wide',
            lg: 'narrow',
            xl: 'narrow',
          }}
        >
          <CollectionGrid
            isLoading={loading}
            pageUrl={pageUrl}
            paginationType={paginationType}
            pagination={
              pagination
                ? {
                    totalPages: pagination.totalPages,
                    total: pagination.totalItems,
                    pageSize: pagination.itemsPerPage,
                    current: pagination.currentPage,
                  }
                : {}
            }
            items={items}
          />
        </Wrapper>
      </Section>
    </LayoutDefault>
  );
};

const HOCCollectionListPage = HOCFilterPageRoute(CollectionListPage, FILTER_ENUM.domain.COLLECTION);

export default function CollectionList(props: any) {
  const match = useRouteMatch();
  const { translatedLocation: location } = useUrlParams();

  return (
    <HOCCollectionListPage
      {...props}
      match={match}
      location={location}
      paginationType={SLIDER}
      domain={FILTER_ENUM.domain[props.routeConfig.filterDomainKey]}
    />
  );
}
